import React from 'react';
import PropTypes from 'prop-types';
import {
  required,
  translate as t,
} from 'admin-on-rest';
import {
  Checkbox,
  RaisedButton,
  Toggle,
} from 'material-ui';
import InfoBadge from './InfoBadge';
import DialogForm from './DialogForm';
import BasicTextInput from '../Quiz/FormInputs/BasicTextInput';
import FileInput from '../inputs/FileInput';
import FileThumbnail from './FileThumbnail';
import AcknowledgmentHistory from './AcknowledgmentHistory';

const TRANSLATION_PREFIX = 'documents.files';

const styles = {
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    flex: 1,
  },
};

// Helper to get a human readable string of the filesize
const fileSizeHumanReadable = (bytes) => {
  if (Number.isNaN(bytes)) {
    return '';
  }
  const threshold = 1000;
  const units = ['B', 'kB', 'MB', 'GB', 'TB'];
  let unitIdx = 0;
  let remaining = Math.abs(bytes);
  while (remaining >= threshold && unitIdx < units.length - 1) {
    remaining /= threshold;
    unitIdx += 1;
  }
  return `${remaining.toFixed(1)} ${units[unitIdx]}`;
};

class FileForm extends React.PureComponent {
  state = {
    name: {
      errorText: null,
      touched: false,
    },
    link: {
      errorText: null,
      touched: false,
    },
  }

  onSave = () => {
    const {
      onSave,
    } = this.props;
    onSave();
  }

  nameOnChange = (ev) => {
    const val = ev.target.value;
    this.change('name', val);
    this.validateName(val);
  }

  validateName = (val) => {
    this.setState({
      name: {
        errorText: required(val, null, this.props),
        touched: true,
      },
    });
  }

  descriptionOnChange = (ev) => {
    this.change('description', ev.target.value);
  }

  fileOnChange = (files) => {
    if (!files || files.length < 1) {
      this.change('fileSrc', '');
      this.change('fileTitle', '');
      this.change('fileSize', '');
    }
    this.change('files', files);
    if (files && files.length > 0 && files[0].rawFile) {
      this.change('fileSize', fileSizeHumanReadable(files[0].rawFile.size));
    }
  }

  clearHistoryOnChange = (_, checked) => {
    this.change('clearHistory', checked);
  }

  hasExternalLinkOnChange = (_, toggled) => {
    this.change('hasExternalLink', toggled);
  }

  linkOnChange = (ev) => {
    const val = ev.target.value;
    this.change('link', val);
    this.validateLink(val);
  }

  validateLink = (val) => {
    this.setState({
      link: {
        errorText: this.props.hasExternalLink && required(val, null, this.props),
        touched: true,
      },
    });
  }

  change = (key, value) => {
    const delta = {};
    delta[key] = value;
    this.props.onChange(delta);
  }

  render() {
    const {
      id, open, name, onClose, onRemove,
      description, fileSrc, fileTitle, fileSize, files,
      acknowledgmentHistory, translate,
      clearHistory, hasExternalLink, link,
    } = this.props;

    let file = [];

    // Previously uploaded file
    if (fileSrc) {
      file = [{
        src: fileSrc,
        title: fileSize ? `${fileTitle} (${fileSize})` : fileTitle,
      }];
    }

    // This property comes from the file input
    // This means the user has uploaded some file currently
    if (files && files.length > 0) {
      const [{
        rawFile,
        title,
        src,
        ...rest
      }] = files;
      file = [{
        rawFile,
        title: `${title} (${fileSizeHumanReadable(rawFile.size)})`,
        src: `${src}/${title}`,
        ...rest,
      }];
    }

    return (
      <DialogForm
        title={`${name}${id ? ` - (${id})` : ''}`}
        open={open}
        onSave={this.onSave}
        confirmSaveTitle={translate(`${TRANSLATION_PREFIX}.saveConfirmTitle`)}
        confirmSaveText={translate(`${TRANSLATION_PREFIX}.saveConfirmText`)}
        confirmOnSave={clearHistory}
        onClose={onClose}
        onRemove={onRemove}
        confirmRemoveTitle={translate(`${TRANSLATION_PREFIX}.removeConfirmTitle`)}
        confirmRemoveText={`${translate(`${TRANSLATION_PREFIX}.removeConfirmText`)} ${name}?`}
        icon={null}
      >
        <div style={styles.row}>
          <div style={{ ...styles.col, marginRight: 24 }}>
            <BasicTextInput
              type="text"
              value={name}
              onTouch={this.nameOnChange}
              errorText={this.state.name.touched && this.state.name.errorText}
              floatingLabelText={translate(`${TRANSLATION_PREFIX}.fields.name`)}
            />
            <BasicTextInput
              type="text"
              value={description}
              onChange={this.descriptionOnChange}
              floatingLabelText={translate(`${TRANSLATION_PREFIX}.fields.description`)}
            />
            {
              id && (
                <>
                  <br />
                  <InfoBadge
                    tooltip={translate(`${TRANSLATION_PREFIX}.readingHistoryInformation`)}
                  >
                    <Checkbox
                      label={translate(`${TRANSLATION_PREFIX}.clearReadingHistory`)}
                      onCheck={this.clearHistoryOnChange}
                      checked={clearHistory}
                    />
                  </InfoBadge>
                </>
              )
            }
            <br />
            <Toggle
              label={translate(`${TRANSLATION_PREFIX}.fields.externalLink`)}
              defaultToggled={hasExternalLink}
              onToggle={this.hasExternalLinkOnChange}
            />
            <br />
            { hasExternalLink && (
              <BasicTextInput
                type="text"
                value={link}
                onTouch={this.linkOnChange}
                errorText={this.state.link.touched && this.state.link.errorText}
                floatingLabelText={translate(`${TRANSLATION_PREFIX}.link`)}
                inputStyle={{
                  fontSize: 13,
                }}
                fullWidth
              />
            )}
            {
              id && hasExternalLink && (
                <>
                  <h4>{translate(`${TRANSLATION_PREFIX}.readingHistory`)}</h4>
                  <AcknowledgmentHistory
                    history={acknowledgmentHistory}
                  />
                </>
              )
            }
            { !hasExternalLink && (
              <>
                <FileInput
                  labelSingle={`${TRANSLATION_PREFIX}.uploadText`}
                  maxSize={100000000}
                  style={{
                    marginBottom: 32,
                  }}
                  input={{
                    value: file,
                    onChange: this.fileOnChange,
                  }}
                >
                  <FileThumbnail source="src" title="title" />
                </FileInput>
                <br />
                {
                  fileSrc && (
                    <RaisedButton
                      label="Ladda ner"
                      href={fileSrc}
                      primary
                      download
                    />
                  )
                }
              </>
            )}
          </div>
          {
            id && !hasExternalLink && (
              <div style={styles.col}>
                <h4>{translate(`${TRANSLATION_PREFIX}.readingHistory`)}</h4>
                <AcknowledgmentHistory
                  history={acknowledgmentHistory}
                />
              </div>
            )
          }
        </div>

      </DialogForm>
    );
  }
}

FileForm.propTypes = {
  open: PropTypes.bool,
  name: PropTypes.string,
  description: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default t(FileForm);
